<script setup lang="ts">
/**
 * The call, full screen on phones (a 360×560 floating card on desktop so the chat stays usable): always
 * dark glass over the app, the assistant's name and the call timer, the light (VoiceCallOrb) reacting to
 * whoever is talking, live captions, a slot for what needs a tap (approvals, questions, browser tasks: the
 * same cards as the chat), and Mute / Chat / End.
 *
 * Tap the light to interrupt (or to send now while you talk); swipe down (or the chevron) to minimize to
 * the pill. Lighthouse's voice is AI-generated: the label says so on the first call and the dialog's
 * accessible name always does.
 */
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import VoiceCallOrb from './VoiceCallOrb.vue'
import ChangesetCard from '@/agent/components/ChangesetCard.vue'
import AskUserForm from '@/agent/components/AskUserForm.vue'
import BrowserTaskCard from '@/agent/components/BrowserTaskCard.vue'
import useAssistantName from '@/composables/useAssistantName'
import { callElapsed, formatCallTime, useVoiceCall } from '@/voice/useVoiceCall'
import { canOpenAppSettings, openAppSettings } from '@/voice/platform'
import { useBackHandler } from '@/native/backStack'

const props = withDefaults(defineProps<{ desktop?: boolean }>(), { desktop: false })

const call = useVoiceCall()
const st = call.state
const view = call.view
const { assistantName } = useAssistantName()

const phase = computed(() => st.phase)
const muted = computed(() => st.flags.muted)
const elapsed = computed(() => formatCallTime(callElapsed()))

const hasCard = computed(() => (phase.value !== 'error' && phase.value !== 'ended')
    && Boolean(view.approval || view.question || view.browserTask || view.browserNeedsYou))
// The light takes what the screen can spare, and steps down to a small mark while a card needs the room.
const vh = ref(typeof window !== 'undefined' ? window.innerHeight : 844)
function onResize() {
    vh.value = window.innerHeight
}
const orbSize = computed(() => {
    if (props.desktop) return hasCard.value ? 84 : 196
    if (hasCard.value) return vh.value < 700 ? 60 : vh.value < 740 ? 76 : Math.round(Math.min(150, vh.value * 0.17))
    return Math.round(Math.max(200, Math.min(300, vh.value * 0.36)))
})

const statusLine = computed(() => {
    switch (phase.value) {
        case 'connecting': return 'Calling…'
        case 'error': return st.error === 'permission' ? 'Microphone is off' : "Couldn't connect"
        case 'ended': return view.startedAt ? `Call ended · ${elapsed.value}` : 'Call ended'
        case 'onHold': return `On hold · ${elapsed.value}`
        default:
            if (st.flags.reconnecting) return `Reconnecting… · ${elapsed.value}`
            return muted.value ? `Muted · ${elapsed.value}` : elapsed.value
    }
})

const hint = computed(() => {
    const p = phase.value
    if (muted.value && ['listening', 'thinking', 'needsApproval', 'userSpeaking'].includes(p)) return "You're muted"
    switch (p) {
        case 'listening': return view.firstTurn ? "Go ahead, I'm listening" : 'Listening'
        case 'userSpeaking': return view.liveCaptions ? '' : 'Listening…'
        case 'sending': return view.liveCaptions ? '' : 'Getting that down…'
        case 'thinking': return 'You can keep talking'
        case 'speaking': return 'Tap the light to interrupt'
        case 'needsApproval': return 'Tap Approve when you’re ready'
        case 'onHold': return 'Come back to the app to pick up where you left off'
        default: return ''
    }
})

/** The lines under the light: what you're saying, what it's doing, or what it's saying. */
const lines = computed(() => {
    const p = phase.value
    // A card on a short screen: the card and the hint are enough.
    if (hasCard.value && (props.desktop || vh.value < 740)) return []
    const c = view.captions
    const out: { key: string; text: string; kind: 'user' | 'agent' | 'progress' | 'quiet' }[] = []
    if (p === 'userSpeaking' || p === 'sending') {
        if (c.user) out.push({ key: 'user', text: c.user, kind: 'user' })
    } else if (p === 'thinking') {
        if (c.user) out.push({ key: 'echo', text: `“${c.user}”`, kind: 'quiet' })
        out.push({ key: 'progress', text: c.progress || 'Thinking', kind: 'progress' })
    } else if (p === 'speaking') {
        if (c.agent) out.push({ key: 'agent', text: c.agent, kind: 'agent' })
    } else if ((p === 'listening' || p === 'needsApproval') && c.agent && !view.firstTurn) {
        out.push({ key: 'last', text: c.agent, kind: 'quiet' })
    }
    return out
})

const orbLabel = computed(() => {
    if (phase.value === 'speaking') return 'Interrupt'
    if (phase.value === 'userSpeaking') return 'Send now'
    return `${assistantName.value} is ${phase.value === 'thinking' ? 'working' : 'listening'}`
})

const errorCopy = computed(() => {
    if (st.error === 'permission') {
        return {
            title: 'Lighthouse needs your microphone',
            body: canOpenAppSettings()
                ? 'Turn on Microphone and Speech Recognition for Lighthouse in Settings, then try again.'
                : 'Allow microphone access for this site in your browser, then try again.',
        }
    }
    if (st.error === 'network') return { title: 'The connection dropped', body: 'Check your connection and try again.' }
    return { title: "The call couldn't start", body: 'Something got in the way of the microphone. Try again in a moment.' }
})

function end() {
    call.end('user')
}

// ── swipe down to minimize (phones) ──
const drag = ref(0)
const dragging = ref(false)
let y0 = 0
let t0 = 0
let pointerId: number | null = null

function onPointerDown(e: PointerEvent) {
    if (props.desktop || e.button > 0) return
    const target = e.target as HTMLElement | null
    if (target?.closest('.vc-card, .vc-controls, .vc-error-actions, button, a, input, textarea')) return
    pointerId = e.pointerId
    y0 = e.clientY
    t0 = performance.now()
    dragging.value = true
}

function onPointerMove(e: PointerEvent) {
    if (!dragging.value || e.pointerId !== pointerId) return
    drag.value = Math.max(0, e.clientY - y0)
}

const settling = ref(false)
let settleTimer: ReturnType<typeof setTimeout> | null = null

function onPointerUp(e: PointerEvent) {
    if (!dragging.value || e.pointerId !== pointerId) return
    const dy = Math.max(0, e.clientY - y0)
    const v = dy / Math.max(1, performance.now() - t0)
    dragging.value = false
    pointerId = null
    if (dy > 110 || (dy > 30 && v > 0.6)) {
        call.minimize()
    } else if (dy > 0) {
        // Spring back.
        settling.value = true
        if (settleTimer) clearTimeout(settleTimer)
        settleTimer = setTimeout(() => (settling.value = false), 420)
    }
    drag.value = 0
}

/** Inline only while dragging / springing back, so the enter / leave transitions own the rest. */
const dragStyle = computed(() => {
    if (dragging.value && drag.value) {
        return { transform: `translateY(${drag.value * 0.9}px) scale(${1 - Math.min(0.06, drag.value / 4000)})`, opacity: String(1 - Math.min(0.35, drag.value / 900)) }
    }
    if (settling.value) return { transition: 'transform 380ms var(--vc-ease), opacity 380ms var(--vc-ease)' }
    return undefined
})

// Phones: the full-screen call is the top layer, so Android back (and Escape) minimizes it first, above
// anything registered underneath it later.
useBackHandler(() => { call.minimize() }, () => !props.desktop, { priority: 100 })

// Desktop: the floating card isn't modal. Escape minimizes it only when focus is in it and nothing else
// (a modal, a sheet) took the key first.
const rootEl = ref<HTMLElement | null>(null)
function onKey(e: KeyboardEvent) {
    if (!props.desktop || e.key !== 'Escape' || e.defaultPrevented) return
    if (!rootEl.value?.contains(document.activeElement)) return
    call.minimize()
}
onMounted(() => {
    window.addEventListener('keydown', onKey)
    window.addEventListener('resize', onResize)
})
onBeforeUnmount(() => {
    window.removeEventListener('keydown', onKey)
    window.removeEventListener('resize', onResize)
    if (settleTimer) clearTimeout(settleTimer)
})
</script>

<template>
    <div
        ref="rootEl"
        class="vc"
        data-call-overlay
        :class="[`is-${phase}`, { 'is-desk': desktop, 'has-card': hasCard, 'is-muted': muted, 'is-dragging': dragging }]"
        role="dialog"
        :aria-modal="desktop ? undefined : 'true'"
        :aria-label="`Voice call with ${assistantName}, an AI voice`"
        :style="dragStyle"
        @pointerdown="onPointerDown"
        @pointermove="onPointerMove"
        @pointerup="onPointerUp"
        @pointercancel="onPointerUp"
    >
        <div class="vc-bg" aria-hidden="true">
            <span class="vc-glow vc-glow-a" />
            <span class="vc-glow vc-glow-b" />
        </div>

        <header class="vc-top">
            <button type="button" class="vc-round" aria-label="Minimize call" @click="call.minimize()">
                <i class="fa-solid fa-chevron-down" />
            </button>
        </header>

        <div class="vc-id">
            <h2 class="vc-name font-lighthouse">{{ assistantName }}</h2>
            <p class="vc-status" :class="{ 'is-warn': st.flags.reconnecting && phase !== 'ended' }">{{ statusLine }}</p>
            <span v-if="view.disclose && phase !== 'error'" class="vc-ai"><i class="fa-solid fa-sparkles" /> AI voice</span>
        </div>

        <div class="vc-stage">
            <button
                type="button"
                class="vc-orb-btn"
                :aria-label="orbLabel"
                :disabled="phase === 'ended' || phase === 'error'"
                @click="call.tapOrb()"
            >
                <VoiceCallOrb :phase="phase" :muted="muted" :levels="call.levels" :size="orbSize" />
                <span v-if="muted && phase !== 'speaking'" class="vc-orb-badge" aria-hidden="true"><i class="fa-solid fa-microphone-slash" /></span>
            </button>
        </div>

        <div v-if="phase === 'error'" class="vc-error" role="alert">
            <p class="vc-error-title">{{ errorCopy.title }}</p>
            <p class="vc-error-body">{{ errorCopy.body }}</p>
            <div class="vc-error-actions">
                <button v-if="st.error === 'permission' && canOpenAppSettings()" type="button" class="vc-pill is-light" @click="openAppSettings()">
                    Open Settings
                </button>
                <button type="button" class="vc-pill" @click="call.retry()">Try again</button>
            </div>
        </div>

        <div v-else class="vc-captions" aria-live="polite">
            <TransitionGroup name="vc-line" tag="div" class="vc-lines">
                <p v-for="l in lines" :key="l.key" class="vc-line" :class="`is-${l.kind}`">
                    <span>{{ l.text }}</span>
                </p>
            </TransitionGroup>
            <p v-if="!view.liveCaptions && phase === 'userSpeaking'" class="vc-dots" aria-hidden="true"><i /><i /><i /></p>
            <Transition name="vc-fade" mode="out-in">
                <p :key="hint" class="vc-hint">{{ hint }}</p>
            </Transition>
        </div>

        <Transition name="vc-card">
            <section v-if="hasCard" class="vc-card" aria-label="Needs you">
                <div class="vc-card-scroll">
                    <ChangesetCard
                        v-if="view.approval"
                        :changeset="view.approval"
                        :busy="view.busy === view.approval.id"
                        @act="(a) => (a === 'approve' ? call.approve(view.approval!.id) : call.reject(view.approval!.id))"
                    />
                    <AskUserForm
                        v-else-if="view.question"
                        :key="`${view.question.id}:${view.answerFailed}`"
                        :question="view.question"
                        :disabled="!!view.busy"
                        @answer="(t) => call.answer(t)"
                    />
                    <BrowserTaskCard
                        v-else-if="view.browserTask"
                        :task="view.browserTask"
                        :session-id="view.sessionId"
                        @open="call.openChat()"
                    />
                    <button v-else type="button" class="vc-browser" @click="call.openChat()">
                        <i class="fa-regular fa-window" /> The browser needs you <span>Open</span>
                    </button>
                </div>
            </section>
        </Transition>

        <footer class="vc-controls">
            <button
                type="button"
                class="vc-ctl"
                :class="{ 'is-on': muted }"
                :aria-pressed="muted"
                :disabled="phase === 'ended' || phase === 'error'"
                @click="call.toggleMute()"
            >
                <span class="vc-ctl-face"><i :class="muted ? 'fa-solid fa-microphone-slash' : 'fa-solid fa-microphone'" /></span>
                <span class="vc-ctl-label">{{ muted ? 'Unmute' : 'Mute' }}</span>
            </button>
            <button
                v-if="view.speakerSupported"
                type="button"
                class="vc-ctl"
                :class="{ 'is-on': st.flags.speaker }"
                :aria-pressed="st.flags.speaker"
                @click="call.setSpeaker(!st.flags.speaker)"
            >
                <span class="vc-ctl-face"><i class="fa-solid fa-volume-high" /></span>
                <span class="vc-ctl-label">Speaker</span>
            </button>
            <button type="button" class="vc-ctl is-end" :disabled="phase === 'ended'" @click="end">
                <span class="vc-ctl-face"><i class="fa-solid fa-phone-hangup" /></span>
                <span class="vc-ctl-label">End</span>
            </button>
            <button type="button" class="vc-ctl" @click="call.openChat()">
                <span class="vc-ctl-face"><i class="fa-solid fa-message-lines" /></span>
                <span class="vc-ctl-label">Chat</span>
            </button>
        </footer>
    </div>
</template>

<style scoped>
/* ── Always dark, whatever the app theme: a deep glass over the app with a warm beacon glow. ── */
.vc {
    --vc-ease: var(--lgx-ease, cubic-bezier(0.32, 0.72, 0, 1));
    --vc-text: rgb(255 255 255 / 0.96);
    --vc-text-2: rgb(255 255 255 / 0.62);
    --vc-text-3: rgb(255 255 255 / 0.42);
    position: fixed;
    inset: 0;
    z-index: var(--z-call, 10090);
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: calc(env(safe-area-inset-top, 0px) + 8px) 20px calc(env(safe-area-inset-bottom, 0px) + 22px);
    color: var(--vc-text);
    overflow: hidden;
    isolation: isolate;
    touch-action: none;
    user-select: none;
    -webkit-user-select: none;
    transform-origin: 50% 30%;
}

.vc-bg {
    position: absolute;
    inset: 0;
    z-index: -1;
    background:
        radial-gradient(120% 70% at 50% 36%, rgb(251 76 31 / 0.24), transparent 62%),
        linear-gradient(180deg, rgb(16 14 18 / 0.97) 0%, rgb(10 9 12 / 0.975) 50%, rgb(4 4 6 / 0.98) 100%);
    -webkit-backdrop-filter: blur(44px) saturate(150%);
    backdrop-filter: blur(44px) saturate(150%);
}
.vc-glow {
    position: absolute;
    border-radius: 50%;
    filter: blur(60px);
    opacity: 0.5;
    will-change: transform;
}
.vc-glow-a {
    width: 70vmin;
    height: 70vmin;
    left: -18vmin;
    top: 8%;
    background: radial-gradient(circle, rgb(251 76 31 / 0.38), transparent 70%);
    animation: vc-drift-a 14s ease-in-out infinite alternate;
}
.vc-glow-b {
    width: 60vmin;
    height: 60vmin;
    right: -20vmin;
    bottom: 12%;
    background: radial-gradient(circle, rgb(255 166 92 / 0.22), transparent 70%);
    animation: vc-drift-b 17s ease-in-out infinite alternate;
}
@keyframes vc-drift-a { to { transform: translate(14vmin, 10vmin) scale(1.12); } }
@keyframes vc-drift-b { to { transform: translate(-12vmin, -14vmin) scale(1.08); } }
.vc.is-error .vc-glow-a { background: radial-gradient(circle, rgb(255 69 58 / 0.3), transparent 70%); }
.vc.is-onHold .vc-glow, .vc.is-ended .vc-glow { opacity: 0.22; }

/* ── top ── */
.vc-top {
    width: 100%;
    max-width: 520px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 44px;
}
.vc-round {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 44px;
    height: 44px;
    border-radius: 999px;
    border: 0;
    color: var(--vc-text);
    font-size: 15px;
    background: rgb(255 255 255 / 0.1);
    box-shadow: inset 0 0 0 0.5px rgb(255 255 255 / 0.16), inset 0 1px 0 rgb(255 255 255 / 0.12);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform 200ms var(--vc-ease), background-color 200ms ease;
}
.vc-round:active { transform: scale(0.92); background: rgb(255 255 255 / 0.18); }

/* ── who ── */
.vc-id {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    margin-top: 14px;
    text-align: center;
}
.vc-name {
    margin: 0;
    font-size: 30px;
    font-weight: 600;
    letter-spacing: -0.02em;
    line-height: 1.1;
    color: var(--vc-text);
}
.vc-status {
    margin: 0;
    font-size: 16px;
    line-height: 1.3;
    font-variant-numeric: tabular-nums;
    color: var(--vc-text-2);
    transition: color 200ms ease;
}
.vc-status.is-warn { color: rgb(255 204 102); }
.vc-ai {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    margin-top: 6px;
    padding: 3px 9px;
    border-radius: 999px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.02em;
    color: rgb(255 255 255 / 0.7);
    background: rgb(255 255 255 / 0.08);
    box-shadow: inset 0 0 0 0.5px rgb(255 255 255 / 0.14);
}
.vc-ai i { font-size: 9px; color: rgb(255 179 140); }

/* ── the light ── */
.vc-stage {
    flex: 1;
    min-height: 0;
    width: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}
.vc-orb-btn {
    position: relative;
    padding: 0;
    border: 0;
    background: none;
    border-radius: 50%;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
    transition: transform 520ms var(--vc-ease);
}
.vc-orb-btn:active:not(:disabled) { transform: scale(0.96); }
.vc-orb-btn:disabled { cursor: default; }
.vc-orb-badge {
    position: absolute;
    right: 22%;
    bottom: 22%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 34px;
    height: 34px;
    border-radius: 999px;
    font-size: 14px;
    color: #fff;
    background: rgb(40 40 44 / 0.92);
    box-shadow: 0 0 0 3px rgb(10 10 12 / 0.9), 0 6px 14px rgb(0 0 0 / 0.4);
}

/* ── captions ── */
.vc-captions {
    width: 100%;
    max-width: 340px;
    min-height: 118px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-end;
    gap: 10px;
    text-align: center;
}
.vc.has-card .vc-captions { min-height: 0; }
.vc.has-card .vc-ai { display: none; }
/* With a card the light keeps its own size and the card takes (and scrolls in) the rest. */
.vc.has-card .vc-stage { flex: 0 0 auto; padding: 10px 0 6px; }
.vc.has-card .vc-card { margin-top: auto; }
.vc-lines {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    width: 100%;
    max-height: calc(1.36em * 3 + 1.4em);
    overflow: hidden;
    justify-content: flex-end;
}
.vc-line {
    margin: 0;
    width: 100%;
    overflow-wrap: anywhere;
    display: -webkit-box;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 3;
    overflow: hidden;
}
.vc-line.is-user { font-size: 23px; line-height: 1.32; font-weight: 500; letter-spacing: -0.01em; color: rgb(255 255 255 / 0.94); }
.vc-line.is-agent { font-size: 21px; line-height: 1.36; letter-spacing: -0.005em; color: rgb(255 255 255 / 0.86); }
.vc-line.is-quiet { font-size: 15px; line-height: 1.4; color: var(--vc-text-3); -webkit-line-clamp: 2; }
.vc-line.is-progress span {
    font-size: 17px;
    line-height: 1.4;
    color: transparent;
    background-image: linear-gradient(100deg, rgb(255 255 255 / 0.5) 0%, rgb(255 255 255 / 0.5) 38%, #fff 50%, rgb(255 255 255 / 0.5) 62%, rgb(255 255 255 / 0.5) 100%);
    background-size: 240% 100%;
    -webkit-background-clip: text;
    background-clip: text;
    animation: vc-shimmer 2.4s linear infinite;
}
@keyframes vc-shimmer { from { background-position: 120% 0; } to { background-position: -120% 0; } }
.vc-hint {
    margin: 0;
    min-height: 18px;
    font-size: 13px;
    line-height: 18px;
    color: var(--vc-text-3);
}
.vc-dots { display: flex; gap: 6px; margin: 0; }
.vc-dots i {
    width: 7px;
    height: 7px;
    border-radius: 50%;
    background: rgb(255 255 255 / 0.8);
    animation: vc-dot 1.1s ease-in-out infinite;
}
.vc-dots i:nth-child(2) { animation-delay: 0.15s; }
.vc-dots i:nth-child(3) { animation-delay: 0.3s; }
@keyframes vc-dot { 0%, 100% { opacity: 0.3; transform: translateY(0); } 50% { opacity: 1; transform: translateY(-3px); } }

.vc-line-enter-active { transition: opacity 260ms ease, transform 360ms var(--vc-ease); }
/* Old lines go at once (no overlap with the new one coming in). */
.vc-line-leave-active { display: none; }
.vc-line-enter-from { opacity: 0; transform: translateY(8px); }
.vc-line-leave-to { opacity: 0; }
.vc-fade-enter-active, .vc-fade-leave-active { transition: opacity 180ms ease; }
.vc-fade-enter-from, .vc-fade-leave-to { opacity: 0; }

/* ── error ── */
.vc-error {
    width: 100%;
    max-width: 330px;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 6px;
    text-align: center;
    margin-bottom: 18px;
}
.vc-error-title { margin: 0; font-size: 19px; font-weight: 600; letter-spacing: -0.01em; }
.vc-error-body { margin: 0; font-size: 14.5px; line-height: 1.45; color: var(--vc-text-2); }
.vc-error-actions { display: flex; gap: 10px; margin-top: 14px; }
.vc-pill {
    min-height: 44px;
    padding: 0 20px;
    border-radius: 999px;
    border: 0;
    font-size: 15px;
    font-weight: 600;
    color: var(--vc-text);
    background: rgb(255 255 255 / 0.14);
    box-shadow: inset 0 0 0 0.5px rgb(255 255 255 / 0.2);
    cursor: pointer;
    transition: transform 200ms var(--vc-ease);
}
.vc-pill.is-light { color: #111; background: #fff; }
.vc-pill:active { transform: scale(0.95); }

/* ── what needs a tap ── */
/* The cards are the chat's own (approval, question, browser task): the call is always dark glass, so the
   card slot carries the dark glass tokens whatever the app theme, and every card inherits them. */
.vc-card {
    color-scheme: dark;
    --agent-card: rgb(255 255 255 / 0.1);
    --agent-card-border: rgb(255 255 255 / 0.14);
    --agent-card-shadow: none;
    --agent-card-shadow-hover: none;
    --agent-hover: rgb(255 255 255 / 0.07);
    --agent-press: rgb(255 255 255 / 0.14);
    --agent-accent-soft: rgb(251 76 31 / 0.18);
    --agent-accent-ring: rgb(251 76 31 / 0.4);
    --agent-user-bubble: rgb(255 255 255 / 0.12);
    --agent-text-1: rgb(255 255 255 / 0.96);
    --agent-text-2: rgb(255 255 255 / 0.74);
    --agent-text-3: rgb(255 255 255 / 0.56);
    --agent-text-4: rgb(255 255 255 / 0.4);
    --lh-surface: rgb(255 255 255 / 0.1);
    --lh-raised: rgb(255 255 255 / 0.12);
    --lh-overlay: rgb(255 255 255 / 0.16);
    --lh-border: rgb(255 255 255 / 0.14);
    --lh-border-subtle: rgb(255 255 255 / 0.08);
    --lh-text-1: rgb(255 255 255 / 0.96);
    --lh-text-2: rgb(255 255 255 / 0.74);
    --lh-text-3: rgb(255 255 255 / 0.56);
    --lh-text-4: rgb(255 255 255 / 0.4);
    --lh-text-5: rgb(255 255 255 / 0.24);
    color: var(--vc-text);
    flex: 0 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
    max-width: 420px;
    margin: 6px 0 16px;
    border-radius: 24px;
    background: rgb(255 255 255 / 0.08);
    box-shadow: inset 0 0 0 0.5px rgb(255 255 255 / 0.16), 0 20px 50px -20px rgb(0 0 0 / 0.7);
    -webkit-backdrop-filter: blur(24px) saturate(140%);
    backdrop-filter: blur(24px) saturate(140%);
    padding: 8px;
    touch-action: pan-y;
}
.vc-card-scroll {
    flex: 1 1 auto;
    min-height: 0;
    overflow-y: auto;
    overscroll-behavior: contain;
    border-radius: 18px;
    user-select: text;
    -webkit-user-select: text;
}
.vc-browser {
    display: flex;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-height: 52px;
    padding: 0 16px;
    border: 0;
    border-radius: 18px;
    font-size: 15px;
    font-weight: 500;
    color: var(--vc-text);
    background: rgb(255 255 255 / 0.08);
    cursor: pointer;
}
.vc-browser span { margin-left: auto; color: rgb(255 179 140); font-weight: 600; }
.vc-card-enter-active { transition: opacity 260ms ease, transform 460ms var(--vc-ease); }
.vc-card-leave-active { transition: opacity 180ms ease, transform 240ms var(--vc-ease); }
.vc-card-enter-from, .vc-card-leave-to { opacity: 0; transform: translateY(40px) scale(0.98); }

/* ── controls ── */
.vc-top, .vc-id, .vc-controls { flex-shrink: 0; }
.vc-controls {
    display: flex;
    align-items: flex-start;
    justify-content: center;
    gap: 28px;
    width: 100%;
    max-width: 420px;
    padding-top: 22px;
}
.vc-ctl {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 8px;
    padding: 0;
    border: 0;
    background: none;
    color: var(--vc-text);
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
}
.vc-ctl:disabled { opacity: 0.4; cursor: default; }
.vc-ctl-face {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 68px;
    height: 68px;
    border-radius: 999px;
    font-size: 23px;
    background: rgb(255 255 255 / 0.12);
    box-shadow: inset 0 0 0 0.5px rgb(255 255 255 / 0.18), inset 0 1px 0 rgb(255 255 255 / 0.14);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    transition: transform 240ms cubic-bezier(0.34, 1.4, 0.64, 1), background-color 200ms ease, color 200ms ease;
}
.vc-ctl:active:not(:disabled) .vc-ctl-face { transform: scale(0.9); }
.vc-ctl.is-on .vc-ctl-face { color: #111; background: #fff; }
.vc-ctl.is-end .vc-ctl-face {
    width: 72px;
    height: 72px;
    margin-top: -2px;
    font-size: 26px;
    color: #fff;
    background: #ff3b30;
    box-shadow: 0 10px 26px -8px rgb(255 59 48 / 0.7), inset 0 1px 0 rgb(255 255 255 / 0.25);
}
.vc-ctl-label { font-size: 12px; font-weight: 500; color: var(--vc-text-2); }

/* Small phones with a card: the card (and its actions) gets the room. A smaller name, light and controls,
   no hint line; the card scrolls in whatever is left. */
@media (max-height: 700px) {
    .vc:not(.is-desk).has-card .vc-id { margin-top: 0; gap: 2px; }
    .vc:not(.is-desk).has-card .vc-name { font-size: 24px; }
    .vc:not(.is-desk).has-card .vc-status { font-size: 14px; }
    .vc:not(.is-desk).has-card .vc-stage { padding: 6px 0 2px; }
    .vc:not(.is-desk).has-card .vc-hint { display: none; }
    .vc:not(.is-desk).has-card .vc-card { margin: auto 0 10px; }
    .vc:not(.is-desk).has-card .vc-controls { padding-top: 10px; gap: 24px; }
    .vc:not(.is-desk).has-card .vc-ctl { gap: 6px; }
    .vc:not(.is-desk).has-card .vc-ctl-face { width: 56px; height: 56px; font-size: 20px; }
    .vc:not(.is-desk).has-card .vc-ctl.is-end .vc-ctl-face { width: 60px; height: 60px; font-size: 22px; }
}

/* ── desktop: a floating card, so the chat stays usable ── */
.vc.is-desk {
    inset: auto 24px 24px auto;
    width: 360px;
    height: 560px;
    padding: 12px 18px 20px;
    border-radius: 30px;
    box-shadow: 0 30px 80px -20px rgb(0 0 0 / 0.55), 0 0 0 0.5px rgb(255 255 255 / 0.12);
    touch-action: auto;
    transform-origin: 100% 100%;
}
.vc.is-desk .vc-bg { border-radius: inherit; }
.vc.is-desk .vc-name { font-size: 22px; }
.vc.is-desk .vc-status { font-size: 14px; }
.vc.is-desk .vc-id { margin-top: 2px; }
.vc.is-desk .vc-round { width: 36px; height: 36px; font-size: 13px; }
.vc.is-desk .vc-captions { min-height: 92px; max-width: 300px; }
.vc.is-desk .vc-line.is-user { font-size: 18px; }
.vc.is-desk .vc-line.is-agent { font-size: 17px; }
.vc.is-desk .vc-ctl-face { width: 54px; height: 54px; font-size: 18px; }
.vc.is-desk .vc-ctl.is-end .vc-ctl-face { width: 58px; height: 58px; font-size: 21px; }
.vc.is-desk .vc-controls { gap: 22px; }
.vc.is-desk .vc-card-scroll { max-height: none; }
/* Room for the card: the call card grows (up to the window) and drops the AI label. */
.vc.is-desk.has-card { height: auto; max-height: min(700px, calc(100vh - 48px)); }
.vc.is-desk.has-card .vc-orb-btn { transform: none; }
.vc.is-desk.has-card .vc-stage { flex: 0 0 auto; padding: 10px 0 4px; }
.vc.is-desk.has-card .vc-captions { margin-top: 0; min-height: 0; }
.vc.is-desk.has-card .vc-card { margin: 10px 0 14px; }

@media (hover: hover) and (pointer: fine) {
    .vc-round:hover { background: rgb(255 255 255 / 0.18); }
    .vc-ctl:hover:not(:disabled) .vc-ctl-face { background: rgb(255 255 255 / 0.2); }
    .vc-ctl.is-end:hover .vc-ctl-face { background: #ff5147; }
    .vc-ctl.is-on:hover .vc-ctl-face { background: #fff; }
}

@media (prefers-reduced-motion: reduce) {
    .vc-orb-btn, .vc-ctl-face { transition: none; }
    .vc-glow, .vc-dots i, .vc-line.is-progress span { animation: none; }
    .vc-line-enter-active, .vc-card-enter-active, .vc-card-leave-active { transition: opacity 200ms ease; }
    .vc-line-enter-from, .vc-card-enter-from, .vc-card-leave-to { transform: none; }
}
</style>
